import { useState } from "react";
import { Calendar, MapPin, Phone } from "lucide-react";
import { getSalonCtas, type CtaKind, type SalonCta } from "@/lib/cta";
import { track } from "@/lib/analytics";
import { SalonPickerSheet, type PickerAction } from "@/components/SalonPickerSheet";
import type { Salon } from "@/data/types";

interface Props {
  salon?: Salon | null;
  /** Which CTAs to render. Default: ["book", "call", "directions"]. */
  kinds?: Exclude<CtaKind, "salonPicker">[];
  ctaLocation: string;
  /** Style variant for the first (primary) button. */
  primary?: "primary" | "brass";
  className?: string;
}

const ICONS: Record<CtaKind, typeof Calendar> = {
  book: Calendar,
  call: Phone,
  directions: MapPin,
  salonPicker: Calendar,
};

export function SalonCtaButtons({
  salon,
  kinds = ["book", "call", "directions"],
  ctaLocation,
  primary = "primary",
  className = "",
}: Props) {
  const ctas = getSalonCtas(salon);
  const [picker, setPicker] = useState<PickerAction | null>(null);

  return (
    <>
      <div className={`flex flex-wrap gap-3 ${className}`}>
        {kinds.map((kind, i) => {
          const cta = ctas[kind];
          const Icon = ICONS[kind];
          const isPrimary = i === 0;
          const cls = cta.disabled
            ? "btn-outline opacity-50 cursor-not-allowed"
            : isPrimary
              ? primary === "brass"
                ? "btn-brass"
                : "btn-primary"
              : kind === "call"
                ? "btn-outline"
                : "btn-ghost";
          return (
            <CtaButton
              key={kind}
              cta={cta}
              icon={Icon}
              className={cls}
              salonName={salon?.shortName}
              ctaLocation={ctaLocation}
              onOpenPicker={() => setPicker(kind as PickerAction)}
            />
          );
        })}
      </div>
      <SalonPickerSheet
        open={picker !== null}
        action={picker ?? "select"}
        onClose={() => setPicker(null)}
      />
    </>
  );
}

interface BtnProps {
  cta: SalonCta;
  icon: typeof Calendar;
  className: string;
  salonName?: string;
  ctaLocation: string;
  onOpenPicker: () => void;
}

function CtaButton({ cta, icon: Icon, className, salonName, ctaLocation, onOpenPicker }: BtnProps) {
  const label = (
    <>
      <Icon size={16} />
      {cta.subLabel ?? cta.label}
    </>
  );

  if (cta.disabled) {
    return (
      <button type="button" disabled aria-disabled className={className}>
        <Icon size={16} /> {cta.label}
      </button>
    );
  }
  if (cta.opensPicker) {
    return (
      <button
        type="button"
        className={className}
        onClick={() => {
          track(cta.event, { cta_location: ctaLocation });
          onOpenPicker();
        }}
      >
        {label}
      </button>
    );
  }
  return (
    <a
      href={cta.href}
      {...(cta.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className={className}
      onClick={() => track(cta.event, { salon_name: salonName, cta_location: ctaLocation })}
    >
      {label}
    </a>
  );
}
